import React from "react";
import debounce from "lodash/debounce";
import styled from "styled-components";
import { useMutation, queryCache } from "react-query";
import {
  Col,
  Row,
  Button,
  Input,
  DatePicker,
  Select,
  Spin,
  Typography,
  Empty,
} from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import { useForm, Controller } from "react-hook-form";
import useSearchUser from "hooks/api/user/useSearchUser";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";
import { removeEmptyFields } from "lib/utils";

const { Option } = Select;
const { Text } = Typography;

const createTask = async ({ todoId, task }) => {
  try {
    const res = await apiAxios.post(`/todos/${todoId}/tasks`, task);
    return res.data.data;
  } catch (e) {
    throw e.response.data.message;
  }
};

const CreateTasks = (props) => {
  // Component Props
  const { jobId, todoId, setShowCreateTask } = props;

  // React Hook Form
  const { control, handleSubmit, reset, setValue, formState } = useForm();

  // Component State
  const [keyword, setKeyword] = React.useState("");
  const [error, setError] = React.useState(null);

  // Component Context
  const {
    state: notificationState,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  // React Query
  const { data, status: searchUserStatus } = useSearchUser(keyword);

  // React Query Mutate
  const [mutateCreate] = useMutation(createTask, {
    onSuccess: (taskData, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          oldData.todo.tasks = [taskData, ...oldData.todo.tasks];
          return oldData;
        }
      );
      reset();
      setShowCreateTask(false);
    },
    onError: (e) => {
      setError(e);
    },
  });

  // Component Function
  const handleSubmitData = async (data) => {
    setError("");
    const dataToSubmit = {
      title: data.title,
      due_date: data.due_date ? data.due_date.format() : "",
    };

    if (data.asignees && data.asignees.length > 0) {
      dataToSubmit["asignees"] = data.asignees.map((asignee) => ({
        id: parseInt(asignee.value),
      }));
    }
    removeEmptyFields(dataToSubmit);
    setLoading("createTask");
    try {
      await mutateCreate({
        todoId,
        jobId,
        task: dataToSubmit,
      });
      finishLoading("createTask");
    } catch (error) {
      finishLoading("createTask");
      console.log(error);
    }
  };

  const formatData = (resData) => {
    const newData = resData?.map((d) => ({
      key: d.id,
      value: d.id,
      text: d.name,
    }));

    return newData;
  };

  const searchUserByKeyword = (keyword) => {
    setKeyword(keyword);
  };
  return (
    <StyledCol xs={24}>
      <Row justify="end">
        <CloseButton
          size="small"
          icon={<CloseCircleFilled style={{ color: "grey" }} />}
          onClick={() => setShowCreateTask(false)}
        >
          Close
        </CloseButton>
      </Row>
      {error && <ErrorContainer>{error}</ErrorContainer>}
      <form onSubmit={handleSubmit(handleSubmitData)}>
        <Row gutter={8}>
          <Col xs={12}>
            <StyledInput>
              <Text className="title">Title</Text>
              <Controller
                as={<Input autoFocus={true} />}
                name="title"
                placeholder="Task title"
                control={control}
              />
            </StyledInput>
          </Col>

          <Col xs={12}>
            <StyledInput>
              <Text className="title">Due date</Text>
              <Controller
                as={DatePicker}
                name="due_date"
                control={control}
                defaultValue=""
                style={{ width: "100%" }}
              />
            </StyledInput>
          </Col>
        </Row>

        <StyledInput>
          <Text className="title">Asign staff</Text>
          <Controller
            name="asignees"
            control={control}
            render={({ value }) => {
              return (
                <Select
                  mode="multiple"
                  placeholder="Assign users"
                  labelInValue
                  filterOption={false}
                  value={value}
                  onSearch={debounce(searchUserByKeyword, 800)}
                  notFoundContent={
                    searchUserStatus === "loading" ? (
                      <Spin size="small" />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )
                  }
                  onChange={(e) => {
                    setKeyword("");
                    setValue("asignees", e, {
                      shouldDirty: true,
                    });
                  }}
                  style={{ width: "100%" }}
                >
                  {formatData(data)?.map((d) => (
                    <Option key={d.value}>{d.text}</Option>
                  ))}
                </Select>
              );
            }}
          />
        </StyledInput>

        <Row gutter={8}>
          <Col xs={18}>
            <Button
              htmlType="submit"
              type="primary"
              loading={notificationState.loading.createTask}
              block
            >
              Create Task
            </Button>
          </Col>
          <Col xs={6}>
            <Button
              htmlType="submit"
              onClick={() => setShowCreateTask(false)}
              block
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </form>
    </StyledCol>
  );
};

const StyledCol = styled(Col)`
  padding: ${(props) => props.theme.spacing[2]};
`;

const StyledInput = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${(props) => props.theme.spacing[2]};

  .title {
    font-size: 0.8rem;
    color: ${(props) => props.theme.colors.grey[600]};
  }
`;

const CloseButton = styled(Button)`
  background-color: ${(props) => props.theme.colors.grey[300]};
  color: ${(props) => props.theme.colors.grey[600]};
  border-color: ${(props) => props.theme.colors.grey[400]};
  border-radius: 9999px;
  padding-left: ${(props) => props.theme.spacing[2]};
  padding-right: ${(props) => props.theme.spacing[2]};
`;

const ErrorContainer = styled.div`
  padding: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) => props.theme.colors.red[100]};
  border: 1px solid ${(props) => props.theme.colors[700]};
  font-weight: 600;
  color: ${(props) => props.theme.colors.red[500]};
  margin-bottom: ${(props) => props.theme.spacing[2]};
  margin-top: ${(props) => props.theme.spacing[2]};
`;

export default CreateTasks;
